import { MDBCol, MDBRow } from "mdb-react-ui-kit";
import Link from "next/link";
import { useRouter } from "next/router";
import useNavBarData from "@/utils/queries/hooks/useNavBarData";
import {
    footerSectionsOne,
    footerSectionsTwo,
} from "@/utils/mockData/homeMocks";
import { useEffect, useMemo } from "react";
import style from "../../../styles/Footer.module.scss";

/* eslint-disable jsx-a11y/anchor-is-valid */
const TERMS_AND_CONDITIONS_PDF_URL =
    "https://d3t4iiwihaed0b.cloudfront.net/terms-condition/Terms_Conditions.pdf";
const TERMS_AND_CONDITIONS_LABEL = "Terms & Conditions";

const FooterLinks = () => {
    const router = useRouter();
    const { data: dropDownData } = useNavBarData();

    const destinationList = useMemo(() => {
        const targetOrder = ["Roma", "Venice", "Florence", "Sorrento"] as const;
        const aliasToCanonical = new Map<string, string>([
            ["rome", "Rome"],
            ["roma", "Roma"],
            ["venezia", "Venice"],
            ["venice", "Venice"],
            ["firenze", "Florence"],
            ["florence", "Florence"],
            ["sorrento", "Sorrento"],
        ]);

        const toCanonical = (name: string): string => {
            const key = name?.trim().toLowerCase();
            return aliasToCanonical.get(key) ?? name;
        };

        const src = dropDownData?.toursByDestination ?? [];

        // Build map canonical -> first matching source item
        const canonicalToItem = new Map<string, (typeof src)[number]>();
        src.forEach(item => {
            const canonical = toCanonical(item.city);
            if (!canonicalToItem.has(canonical)) {
                canonicalToItem.set(canonical, item);
            }
        });

        // Return in the exact requested order with display label set
        return targetOrder
            .map(name => {
                const found = canonicalToItem.get(name);
                if (!found) return undefined;
                return { ...found, displayCity: name } as typeof found & {
                    displayCity: string;
                };
            })
            .filter(Boolean) as Array<
            (typeof src)[number] & { displayCity: string }
        >;
    }, [dropDownData]);

    const path = "/tours-destinations";

    const handleRouting = (tour: string | null, destination: string | null) => {
        const url = {
            pathname: path,
            query: {
                tourType: tour,
                destination,
            },
        };
        if (router.pathname !== path) {
            router.push(url);
        } else {
            router.replace(url).then(() => router.reload());
        }
    };

    useEffect(() => {
        // IUBENDA DEVE AVERE DISPONIBILI IN PAGINA GLI ELEMENTI SUI QUALI ATTACHARE EVENT LISTENER
        // Carica iubenda dopo che la pagina è interattiva per ridurre TBT
        const loadIubenda = () => {
            const script = document.createElement("script");
            script.src = "https://cdn.iubenda.com/iubenda.js";
            script.async = true;
            document.body.appendChild(script);
        };

        // Carica iubenda quando il browser è inattivo o dopo un delay
        if (
            typeof window !== "undefined" &&
            "requestIdleCallback" in window &&
            typeof window.requestIdleCallback === "function"
        ) {
            window.requestIdleCallback(loadIubenda, { timeout: 3000 });
        } else {
            setTimeout(loadIubenda, 3000);
        }
    }, []);

    return (
        <>
            <MDBCol lg="6" xl="6" className={`mb-4 ${style.desktop}`}>
                <MDBRow style={{ width: "100%" }}>
                    {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                    {footerSectionsOne.map(section => (
                        <div
                            className="mx-auto mb-4"
                            key={section.id}
                            style={{
                                width: "fit-content",
                            }}
                        >
                            <h6
                                className={`${style.text_title} text-start fw-bold mb-4`}
                            >
                                {section.title}
                            </h6>
                            {section.links.map(link => {
                                return link.title ===
                                    TERMS_AND_CONDITIONS_LABEL ? (
                                    <div
                                        className={`${style.text_content} text-start`}
                                        key={link.id}
                                    >
                                        <a
                                            href={TERMS_AND_CONDITIONS_PDF_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-reset"
                                        >
                                            {link.title}
                                        </a>
                                    </div>
                                ) : link.title === "Notice at Collection" ? (
                                    <div
                                        className={`${style.text_content} text-start`}
                                        key={link.id}
                                    >
                                        <a
                                            style={{
                                                color: "#777e90 !important",
                                            }}
                                            href="#"
                                            className="iubenda-cs-uspr-link"
                                        >
                                            Notice at Collection
                                        </a>
                                    </div>
                                ) : link.id === "Policy & Privacy" ? (
                                    <div
                                        className={`${style.text_content} text-start`}
                                        key={link.id}
                                    >
                                        <a
                                            href="https://www.iubenda.com/privacy-policy/31261392"
                                            className="iubenda-nostyle iubenda-noiframe iubenda-embed iub-legal-only iubenda-noiframe "
                                            title="Privacy Policy "
                                        >
                                            Privacy Policy
                                        </a>
                                    </div>
                                ) : link.id === "Cookie" ? (
                                    <div
                                        className={`${style.text_content} text-start`}
                                        key={link.id}
                                    >
                                        <a
                                            href="https://www.iubenda.com/privacy-policy/31261392/cookie-policy"
                                            className="iubenda-nostyle iubenda-noiframe iubenda-embed iubenda-noiframe "
                                            title="Cookie Policy "
                                        >
                                            Cookie Policy
                                        </a>
                                    </div>
                                ) : (
                                    <div
                                        className={`${style.text_content} text-start`}
                                        key={link.id}
                                    >
                                        {link.id === "Manage & consents" ? (
                                            <a
                                                className="iubenda-tp-btn iubenda-cs-preferences-link"
                                                href="#"
                                            >
                                                Manage Consents
                                            </a>
                                        ) : (
                                            <Link
                                                href={link.href}
                                                className="text-reset"
                                                target={
                                                    link.newPage ? "_blank" : ""
                                                }
                                            >
                                                {link.title}
                                            </Link>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    {footerSectionsTwo.map(section => (
                        <div
                            className="mx-auto mb-4"
                            key={section.id}
                            style={{ width: "fit-content" }}
                        >
                            <h6
                                className={`${style.text_title} text-start fw-bold mb-4`}
                            >
                                {section.title}
                            </h6>
                            {section.id === "Tours" &&
                                dropDownData?.toursHome?.map(link => (
                                    <div
                                        className={`${style.text_content} text-start`}
                                        key={link.tourType}
                                    >
                                        <span
                                            onClick={() =>
                                                handleRouting(link.type, null)
                                            }
                                            className="text-reset"
                                        >
                                            {link.tourType}
                                        </span>
                                    </div>
                                ))}
                            {section.id === "Destinations" &&
                                (destinationList && destinationList.length > 0
                                    ? destinationList
                                    : ([
                                          { city: "Rome" },
                                          { city: "Venice" },
                                          { city: "Florence" },
                                          { city: "Sorrento" },
                                      ] as Array<{
                                          city: string;
                                          displayCity?: string;
                                      }>)
                                ).map(
                                    (link: {
                                        city: string;
                                        displayCity?: string;
                                    }) => (
                                        <div
                                            className={`${style.text_content} text-start`}
                                            key={link.city}
                                        >
                                            <span
                                                onClick={() =>
                                                    handleRouting(
                                                        null,
                                                        link.displayCity ??
                                                            link.city
                                                    )
                                                }
                                                className="text-reset"
                                            >
                                                {link.displayCity ?? link.city}
                                            </span>
                                        </div>
                                    )
                                )}
                        </div>
                    ))}
                </MDBRow>
            </MDBCol>
            {/* <MDBCol lg="3" xl="3" className={`mx-auto mb-4 ${style.desktop}`}>
                <MDBRow style={{ width: "100%" }}></MDBRow>
            </MDBCol> */}
        </>
    );
};
export default FooterLinks;
